import React, { useCallback, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import styles from "./index.module.scss";
import optionStyles from "./option.module.scss";
import searchStyles from "./search.module.scss";
import { FixDropdownOptionItem } from "../fix-dropdown/FixDropdownOption";
import FixDropdownHead from "../fix-dropdown-head";
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Input } from "@headlessui/react";
import { Icon } from "@/elements-react/fix-icon";

export interface FixDropdownInputProps {
  items: FixDropdownOptionItem[];
  onSelect?: (item: { value: string | number; label: string }, index: number) => void;
  onSearch?: (value: string) => void;
  placeholder?: string;
  customError?: string;
  helpText?: string | null;
  controlled?: boolean;
  value?: string;
  searchable?: boolean;
  loading?: boolean;
}

export const FixDropdownInput: React.FC<FixDropdownInputProps> = ({
  items,
  onSelect,
  onSearch,
  placeholder = "",
  customError = "",
  helpText = null,
  controlled = false,
  searchable = false,
  loading = false,
  value = "",
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [selected, setSelected] = useState<{ value: string | number; label: string } | undefined>();
  const [searchTerm, setSearchTerm] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const helpMessage = customError || helpText;

  const options = useMemo(() => {
    const lowerCaseTerm = searchTerm.trim().toLowerCase();
    const found = { starting: [], including: [] } as {
      starting: FixDropdownOptionItem[];
      including: FixDropdownOptionItem[];
    };
    const matchOption = (item: FixDropdownOptionItem) => {
      const labelIndex = item.label.toLowerCase().indexOf(lowerCaseTerm);
      const valueIndex = item.value.toString().toLowerCase().indexOf(lowerCaseTerm);
      if (labelIndex === 0) return "label-starting";
      if (labelIndex > 0) return "label-including";
      if (valueIndex === 0) return "value-starting";
      if (valueIndex > 0) return "value-including";
      return null;
    };
    for (const item of items) {
      const matched = lowerCaseTerm.length ? matchOption(item) : "including";
      if (!matched) continue;
      if (matched.includes("starting")) {
        found.starting.push(item);
      } else {
        found.including.push(item);
      }
    }
    found.starting.sort((a, b) => a.label.localeCompare(b.label));
    found.including.sort((a, b) => a.label.localeCompare(b.label));
    return [...found.starting, ...found.including];
  }, [items, searchTerm]);

  const selectedFullItem = useMemo(() => {
    if (controlled) {
      return items.find((item) => item.selected);
    }
    return items.find((item) => item.value === selected?.value) ?? items.find((item) => item.selected);
  }, [controlled, items, selected]);

  const handleOptionClick = useCallback(
    (item: { label: string; value: string | number }, index: number) => {
      setSelected(item);
      setShowDropdown(false);
      onSelect?.(item, index);
    },
    [onSelect],
  );

  const handleInputClick = useCallback(() => {
    setShowDropdown((prev) => !prev);
  }, []);

  const handleInputChange = useCallback(
    (e: any) => {
      const value = e.target?.value ?? "";
      setSearchTerm(value);
      onSearch?.(value);
    },
    [onSearch],
  );

  const handleOutsideClick = useCallback((event: MouseEvent) => {
    if (!containerRef.current?.contains(event.target as Node)) {
      setShowDropdown(false);
    }
  }, []);

  React.useEffect(() => {
    if (showDropdown) {
      document.addEventListener("mousedown", handleOutsideClick);
      return () => document.removeEventListener("mousedown", handleOutsideClick);
    }
  }, [showDropdown, handleOutsideClick]);

  const content = (<div style={{display: 'contents'}}>
    <div className={styles.dropdownOptions}>
      {options.map((item, index) => (
        <div
          key={item.value}
          className={classNames(optionStyles.option, {
            [optionStyles.optionSelected]: item.value === selectedFullItem?.value,
          })}
          onClick={() => handleOptionClick({ value: item.value, label: item.label }, index)}
        >
          {item.icon && <span>{item.icon}</span>}
          <span className={optionStyles.optionLabel}>{item.label ?? item.value}</span>
          {item.value === selectedFullItem?.value && (
            <span className={optionStyles.selectedIcon}>
                  <Icon name="check" />
                </span>
          )}
        </div>
      ))}
    </div>
  </div>);

  return (
    <div className={styles.dropdownBox} ref={containerRef} onClick={handleInputClick}>
      <FixDropdownHead
        label={selectedFullItem?.label || placeholder}
        active={showDropdown}
        icon={typeof selectedFullItem?.icon === "string" ? selectedFullItem.icon : "custom"}
      >
        {typeof selectedFullItem?.icon !== "string" && selectedFullItem?.icon}
      </FixDropdownHead>
      <div className={classNames(styles.dropdownContent, { [styles.dropdownContentOpen]: showDropdown })} onClick={(e) => e.stopPropagation()}>
        {searchable && (
          <div className={searchStyles.dropdownSearch}>
            <Icon name={'search-variant'} className={searchStyles.searchIcon} />
            <Input value={searchTerm} onInput={handleInputChange} placeholder="Search" className={searchStyles.searchInput} />
          </div>
        )}
        {loading ? <Icon className={styles.loader} name={'loader'} /> : content}
      </div>
    </div>
  );
};
